import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fetchAnalytics } from "../../store/actions/userActions";
import { billingPathFor, landingPathFor } from "../auth/v2/landingPath";
import { Badge, BmModal, Btn, Checkbox, Overline } from "../ForBrands/ui/BrandUI";
import { ListRule, PayoutRow } from "../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../ForBrands/useIsPhone";
import "../ForBrands/brandMode.css";

// Subscription status prompt (V2). Opens over a page for a subscription
// that has expired or renews / ends within three days, unless it was
// snoozed today. It closes itself (onClose) when there is nothing to say.
//
// In the phone theme (Athlete and Coach Mode below 640px) BmModal opens it as
// a bottom sheet, whose footer stacks every child as a 52px pill. There the
// snooze checkbox moves to the end of the body, Renew / Subscribe joins
// Close in the footer (on top, as the primary action), and the payment
// history reads as rows instead of a four-column table. Elsewhere the
// dialog is unchanged.

const DISMISS_KEY = "subscriptionStatusModalLastDismissed";
const NO_PERMISSIONS = [];
const NO_ANALYTICS = {};

const STATUS_BADGES = {
  active: { tone: "outline", dot: "bg-bm-positive", label: "Active" },
  trial: { tone: "outline", dot: "bg-bm-info", label: "Trial" },
  expired: { tone: "critical", label: "Expired" },
};

const PAYMENT_DOTS = { success: "bg-bm-positive", pending: "bg-bm-attention" };

const BENEFITS = ["Complete profile access", "Advanced analytics", "Premium features"];

// Where Renew / Subscribe goes, by role (the billing page inside each shell):
// Coach Mode for coaches, Brand Mode for a brand/agency account that is not
// an admin or Cache AI staff, the Player Index for the other business roles.
const renewPathFor = (permissions) => {
  const has = (p) => permissions.includes(p);
  if (has("IS_COACH")) return "/coach/billing";
  if (has("IS_BRAND_AGENCY") && !has("IS_ADMIN") && !has("IS_CACHE_AI")) return "/brand/billing";
  if (["IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD"].some(has)) return "/business/billing";
  return "/athlete/billing";
};

const dismissedToday = () => {
  try {
    const last = localStorage.getItem(DISMISS_KEY);
    return !!last && new Date(last).toDateString() === new Date().toDateString();
  } catch {
    return false;
  }
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : format(date, "MMM dd, yyyy");
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(amount);

const capitalize = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "");

const DotBadge = ({ tone = "outline", dot, children }) => (
  <Badge tone={tone}>
    {dot && <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${dot}`} />}
    {children}
  </Badge>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const SubscriptionStatusModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const analytics = useSelector((state) => state.user?.analytics || NO_ANALYTICS);
  const permissions = useSelector((state) => state.user?.user?.permissions || state.auth?.user?.permissions || NO_PERMISSIONS);
  const [snooze, setSnooze] = useState(false);
  const phone = usePhoneTheme();
  const renewRef = useRef(null);
  const closeRef = useRef(null);

  const status = analytics.subscription_status;
  const expired = status === "expired";
  const renewable = expired || status === "trial";
  const needed = expired || ((status === "active" || status === "trial") && (analytics.days_remaining || 0) <= 3);
  const snoozed = dismissedToday();

  useEffect(() => {
    dispatch(fetchAnalytics());
  }, [dispatch]);

  // Nothing to say (snoozed today, or the status is fine once it loaded).
  useEffect(() => {
    if (snoozed || (status !== undefined && !needed)) onClose();
  }, [snoozed, status, needed, onClose]);

  if (!isOpen || !needed || snoozed) return null;

  const handleClose = () => {
    if (snooze) {
      try {
        localStorage.setItem(DISMISS_KEY, new Date().toISOString());
      } catch {
        // Storage blocked: the prompt simply shows again next time.
      }
    }
    onClose();
  };

  const badge = STATUS_BADGES[status] || { tone: "passive", label: "Unknown" };
  const history = Array.isArray(analytics.payment_history) ? analytics.payment_history : [];
  const historyPath = billingPathFor(permissions) || landingPathFor(permissions);

  const snoozeBox = (
    <Checkbox checked={snooze} onChange={setSnooze} label="Snooze until tomorrow" className={phone ? "min-h-[44px]" : "mr-auto"} />
  );
  const renewButton = renewable ? (
    <Btn
      ref={renewRef}
      onClick={() => {
        handleClose();
        navigate(renewPathFor(permissions));
      }}
    >
      {expired ? "Renew subscription" : "Subscribe now"}
    </Btn>
  ) : null;
  const closeButton = (
    <Btn ref={closeRef} variant="secondary" onClick={handleClose}>
      Close
    </Btn>
  );

  // Focus opens on Renew / Subscribe when there is one, else on Close (never
  // on the snooze checkbox, where Space would snooze by accident).
  return (
    <BmModal
      title="Subscription status"
      onClose={handleClose}
      width={520}
      initialFocusRef={renewable ? renewRef : closeRef}
      footer={
        phone ? (
          <>
            {closeButton}
            {renewButton}
          </>
        ) : (
          <>
            {snoozeBox}
            {closeButton}
          </>
        )
      }
    >
      <div className="flex flex-col gap-5">
        {phone ? (
          <div className="flex">
            <DotBadge tone={badge.tone} dot={badge.dot}>
              {badge.label}
            </DotBadge>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DotBadge tone={badge.tone} dot={badge.dot}>
              {badge.label}
            </DotBadge>
            {renewButton}
          </div>
        )}

        {status === "trial" && (
          <p className="bm-body-m text-bm-muted">
            Your trial access ends in <span className="bm-label-l text-bm-ink">{analytics.days_remaining}</span> days
          </p>
        )}
        {status === "active" && (
          <p className="bm-body-m text-bm-muted">
            Your subscription will renew in <span className="bm-label-l text-bm-ink">{analytics.days_remaining}</span> days
          </p>
        )}
        {expired && <p className="bm-body-m text-bm-critical">Your subscription has expired. Renew now to continue accessing all features.</p>}

        <dl className="grid grid-cols-2 gap-y-2 border-t border-bm-border pt-4 bm-body-m">
          <dt className="text-bm-muted">Trial end date</dt>
          <dd className="text-bm-ink">{formatDate(analytics.trial_end)}</dd>
          <dt className="text-bm-muted">Next payment</dt>
          <dd className="text-bm-ink">{formatDate(analytics.next_payment)}</dd>
        </dl>

        {history.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-bm-border pt-4">
            <Overline>Payment history</Overline>
            {phone ? (
              <div className="rounded-[8px] border border-bm-border px-4">
                {history.map((payment, index) => (
                  <React.Fragment key={`${payment.date || ""}-${index}`}>
                    {index > 0 && <ListRule />}
                    <PayoutRow
                      label={formatDate(payment.date)}
                      sub={payment.plan || "Standard"}
                      amount={formatCurrency(payment.amount)}
                      badge={<DotBadge dot={PAYMENT_DOTS[payment.status] || "bg-bm-critical"}>{capitalize(payment.status)}</DotBadge>}
                    />
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="max-h-40 overflow-y-auto rounded-[8px] border border-bm-border">
                <table className="bm-table-compact w-full border-collapse text-left">
                  <thead>
                    <tr>
                      <th scope="col" className="bm-th">Date</th>
                      <th scope="col" className="bm-th">Amount</th>
                      <th scope="col" className="bm-th">Status</th>
                      <th scope="col" className="bm-th">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((payment, index) => (
                      <tr key={`${payment.date || ""}-${index}`} className="hover:bg-bm-subtle">
                        <td className="bm-td whitespace-nowrap bm-body-m text-bm-muted">{formatDate(payment.date)}</td>
                        <td className="bm-td whitespace-nowrap bm-label-l text-bm-ink">{formatCurrency(payment.amount)}</td>
                        <td className="bm-td whitespace-nowrap">
                          <DotBadge dot={PAYMENT_DOTS[payment.status] || "bg-bm-critical"}>{capitalize(payment.status)}</DotBadge>
                        </td>
                        <td className="bm-td whitespace-nowrap bm-body-m text-bm-muted">{payment.plan || "Standard"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {history.length > 5 && historyPath && (
              <div className="flex justify-end">
                <Btn variant="link" size="sm" onClick={() => navigate(historyPath)}>
                  View full history
                </Btn>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px]">
          <span className="bm-label-l text-bm-ink">Subscription benefits</span>
          <ul className="flex flex-col gap-2">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2 bm-body-m text-bm-muted">
                <span className="text-bm-orange">
                  <CheckIcon />
                </span>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {phone && snoozeBox}
      </div>
    </BmModal>
  );
};

export default SubscriptionStatusModal;
